import { Button, cn } from '@ludus/ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  formatMonthTitle,
  isSameDay,
  isoWeekDates,
  monthGrid,
  startOfUTCDay,
} from '../../utils/weekRange.ts'

interface MiniCalendarProps {
  visibleDate: Date
  onSelect: (date: Date) => void
}

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export function MiniCalendar({ visibleDate, onSelect }: MiniCalendarProps) {
  const { t } = useTranslation()
  const [cursor, setCursor] = useState<Date>(() => startOfUTCDay(visibleDate))
  const today = startOfUTCDay(new Date())

  useEffect(() => {
    setCursor(startOfUTCDay(visibleDate))
  }, [visibleDate])

  const days = monthGrid(cursor)
  const visibleWeekDates = isoWeekDates(visibleDate)
  const cursorMonth = cursor.getUTCMonth()

  return (
    <div className="flex flex-col gap-2 px-3 pt-1">
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
          {formatMonthTitle(cursor)}
        </span>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label={t('pages.calendar.toolbar.prev')}
            onClick={() =>
              setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursorMonth - 1, 1)))
            }
          >
            <ChevronIcon dir="left" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label={t('pages.calendar.toolbar.next')}
            onClick={() =>
              setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursorMonth + 1, 1)))
            }
          >
            <ChevronIcon dir="right" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center text-[10px] font-medium uppercase text-muted">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0.5 text-xs">
        {days.map((d, i) => {
          const inMonth = d.getUTCMonth() === cursorMonth
          const isToday = isSameDay(d, today)
          const inVisibleWeek = visibleWeekDates.some((w) => isSameDay(w, d))
          const positionInWeek = i % 7
          const isFirstInWeek = positionInWeek === 0
          const isLastInWeek = positionInWeek === 6
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => onSelect(d)}
              className={cn(
                'group inline-flex h-7 w-full cursor-pointer items-center justify-center text-center tabular-nums transition-colors',
                'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                inVisibleWeek
                  ? cn(
                      'bg-sidebar-active-bg/80',
                      isFirstInWeek && 'rounded-l-sm',
                      isLastInWeek && 'rounded-r-sm',
                    )
                  : 'hover:bg-sidebar-hover-bg',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors',
                  inMonth ? 'text-foreground' : 'text-muted/60',
                  isToday && 'bg-primary font-semibold text-primary-foreground',
                )}
              >
                {d.getUTCDate()}
              </span>
            </button>
          )
        })}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 h-7 self-start text-xs"
        onClick={() => {
          setCursor(today)
          onSelect(today)
        }}
      >
        {t('pages.calendar.toolbar.today')}
      </Button>
    </div>
  )
}

function ChevronIcon({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {dir === 'left' ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  )
}
